import React, { useState } from "react";
import { FaArrowRight, FaTruck, FaBus } from "react-icons/fa";
import tmsImage from "../images/s4.png";
import tmsImage1 from "../images/s8.png";

const tmsFeatures = [
  "Real-time tracking and updates",
  "Route optimization for cost savings",
  "Integration with ERP & fleet systems",
  "Detailed analytics and reporting",
];

const fmsFeatures = [
  "Vehicle maintenance scheduling",
  "Driver performance monitoring",
  "Fuel consumption tracking",
  "Compliance & documentation management",
];

function TransportManagementSystem() {
  const [activeTab, setActiveTab] = useState("tms");

  const scrollToSection = (id) => {
    setActiveTab(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* ── Scoped styles ── */}
      <style>{`
        .tms-wrapper {
          font-family: 'Segoe UI', sans-serif;
          padding: 2rem 1rem;
        }

        /* ── Nav Pills ── */
        .tms-nav {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 3rem;
          list-style: none;
          padding: 0;
        }
        .tms-nav-btn {
          background: #f1f5f9;
          border: 2px solid transparent;
          border-radius: 50px;
          padding: 0.55rem 1.5rem;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          color: #475569;
          transition: all 0.25s ease;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .tms-nav-btn:hover {
          background: #e2e8f0;
        }
        .tms-nav-btn.active-tms {
          background: #dbeafe;
          border-color: #3b82f6;
          color: #1d4ed8;
        }
        .tms-nav-btn.active-fms {
          background: #dcfce7;
          border-color: #22c55e;
          color: #15803d;
        }

        /* ── Section layout ── */
        .tms-section {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 2rem;
          margin-bottom: 3.5rem;
        }
        .tms-section.reverse {
          flex-direction: row-reverse;
        }
        .tms-text {
          flex: 1 1 300px;
        }
        .tms-image-wrap {
          flex: 1 1 280px;
          display: flex;
          justify-content: center;
        }

        /* ── Responsive image ── */
        .tms-img {
          width: 100%;
          max-width: 420px;
          height: auto;
          border-radius: 16px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          display: block;
          transition: transform 0.35s ease, box-shadow 0.35s ease;
          object-fit: cover;
        }
        .tms-img:hover {
          transform: scale(1.04);
          box-shadow: 0 14px 40px rgba(0, 0, 0, 0.18);
        }

        /* ── Typography ── */
        .tms-title-blue {
          font-size: clamp(1.4rem, 3vw, 1.9rem);
          font-weight: 700;
          color: #1d4ed8;
          margin-bottom: 0.75rem;
        }
        .tms-title-green {
          font-size: clamp(1.4rem, 3vw, 1.9rem);
          font-weight: 700;
          color: #15803d;
          margin-bottom: 0.75rem;
        }
        .tms-desc {
          color: #64748b;
          font-size: 0.97rem;
          line-height: 1.7;
          margin-bottom: 1.2rem;
        }

        /* ── Feature list ── */
        .tms-features {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .tms-features li {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          margin-bottom: 0.65rem;
          font-size: 0.95rem;
          color: #334155;
        }
        .icon-blue { color: #3b82f6; margin-top: 3px; flex-shrink: 0; }
        .icon-green { color: #22c55e; margin-top: 3px; flex-shrink: 0; }

        /* ── Divider ── */
        .tms-divider {
          border: none;
          border-top: 1.5px solid #e2e8f0;
          margin: 2rem 0 3rem;
        }

        /* ── Mobile breakpoint ── */
        @media (max-width: 576px) {
          .tms-img {
            max-width: 100%;
            border-radius: 12px;
          }
          .tms-section,
          .tms-section.reverse {
            flex-direction: column;
          }
          .tms-nav-btn {
            font-size: 0.85rem;
            padding: 0.45rem 1.1rem;
          }
        }

        @media (min-width: 577px) and (max-width: 768px) {
          .tms-img {
            max-width: 340px;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .tms-img {
            max-width: 380px;
          }
        }

        @media (min-width: 1025px) {
          .tms-img {
            max-width: 420px;
          }
        }
      `}</style>

      <div className="tms-wrapper">
        {/* ── Navigation Tabs ── */}
        <ul className="tms-nav" role="tablist">
          <li>
            <button
              className={`tms-nav-btn ${activeTab === "tms" ? "active-tms" : ""}`}
              onClick={() => scrollToSection("tms")}
              aria-selected={activeTab === "tms"}
              role="tab"
            >
              <FaTruck /> Transport Management System
            </button>
          </li>
          <li>
            <button
              className={`tms-nav-btn ${activeTab === "fms" ? "active-fms" : ""}`}
              onClick={() => scrollToSection("fms")}
              aria-selected={activeTab === "fms"}
              role="tab"
            >
              <FaBus /> Fleet Management System
            </button>
          </li>
        </ul>

        {/* ── Section 1: TMS ── */}
        <div id="tms" className="tms-section">
          <div className="tms-text">
            <h2 className="tms-title-blue">Transport Management System</h2>
            <p className="tms-desc">
              Our <strong>Transport Management System (TMS)</strong> is engineered
              to optimize and streamline logistics. It empowers businesses with{" "}
              <strong>real-time visibility, intelligent route optimization</strong>,
              and <strong>automated workflows</strong>, ensuring efficiency and
              cost-effectiveness.
            </p>
            <ul className="tms-features">
              {tmsFeatures.map((item, i) => (
                <li key={i}>
                  <FaArrowRight className="icon-blue" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="tms-image-wrap">
            <img
              src={tmsImage}
              alt="Transport Management System dashboard"
              className="tms-img"
              loading="lazy"
            />
          </div>
        </div>

        <hr className="tms-divider" />

        {/* ── Section 2: FMS ── */}
        <div id="fms" className="tms-section reverse">
          <div className="tms-text">
            <h2 className="tms-title-green">Fleet Management System</h2>
            <p className="tms-desc">
              Our <strong>Fleet Management System</strong> helps you track, monitor,
              and maintain vehicles effectively, ensuring{" "}
              <strong>maximum uptime</strong> and <strong>better ROI</strong> on
              your assets.
            </p>
            <ul className="tms-features">
              {fmsFeatures.map((item, i) => (
                <li key={i}>
                  <FaArrowRight className="icon-green" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="tms-image-wrap">
            <img
              src={tmsImage1}
              alt="Fleet Management System dashboard"
              className="tms-img"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default TransportManagementSystem;